import {
  type EnablePaymentMethodInput,
  type PaymentMethodDto,
  type PlatformBillingSettingsDto,
  type PublicPaymentMethodDto,
  type SavePaymentMethodInput,
  type SavePlatformBillingInput,
  type StartUpgradeInput,
  type SubscriptionOrderDto,
  type AdminPlanDto,
  type AssignPlanInput,
  type PlanDto,
  type SubscriptionDto,
  type UpdatePlanInput,
  type CategoryDto,
  type CheckoutPreviewDto,
  type CheckoutResultDto,
  type CouponDto,
  type CreateCheckoutInput,
  type CreateCouponInput,
  type CreateCustomerInput,
  type CreateProductInput,
  type CreateSalesPageInput,
  type CustomerDto,
  type CustomerNoteDto,
  type DashboardMetricsDto,
  type InviteStaffInput,
  type LoginInput,
  type MerchantSettingsDto,
  type OnboardingStep,
  type OrderDto,
  type OrderSummaryDto,
  type PreviewCheckoutInput,
  type ProductDto,
  type PublicProductDto,
  type PublicSalesPageDto,
  type AdminActivityDto,
  type AdminStoreDetailDto,
  type AdminStoreRowDto,
  type PlatformOverviewDto,
  type PlatformUserDto,
  type PublicStoreDto,
  type RegisterInput,
  type SalesPageContent,
  type SalesPageDto,
  type SalesPageWithContentDto,
  type SessionDto,
  type ShippingMethodDto,
  type ShippingZoneDto,
  type StaffInvitationDto,
  type StaffMemberDto,
  type SupportAccessDto,
  type SupportAccessLogDto,
  type UpdateCouponInput,
  type UpdateMerchantSettingsInput,
  type UpdateProductInput,
} from '@musleempay/shared';
import { type ApiResult, api, newIdempotencyKey } from '../lib/api-client.js';

/**
 * API service layer.
 *
 * Components never call `api.*` directly; they use a hook, and the hook calls
 * one of these functions. That keeps every endpoint path in one file and makes
 * a route change a one-line edit rather than a search across components.
 */

type Query = Record<string, string | number | boolean | undefined | null>;

// -----------------------------------------------------------------------------
// Auth
// -----------------------------------------------------------------------------

export const authService = {
  register: (input: RegisterInput) =>
    api.post<{ userId: string; merchantId: string; message: string }>('/api/v1/auth/register', input),

  login: (input: LoginInput) =>
    api.post<SessionDto & { csrfToken: string }>('/api/v1/auth/login', input),

  logout: () => api.post<{ ok: true }>('/api/v1/auth/logout'),

  session: (signal?: AbortSignal) => api.get<SessionDto>('/api/v1/auth/session', undefined, signal),

  refresh: () => api.post<SessionDto & { csrfToken: string }>('/api/v1/auth/refresh'),

  verifyEmail: (token: string) =>
    api.post<{ message: string }>('/api/v1/auth/verify-email', { token }),

  resendVerification: (email: string) =>
    api.post<{ message: string }>('/api/v1/auth/resend-verification', { email }),

  forgotPassword: (email: string) =>
    api.post<{ message: string }>('/api/v1/auth/forgot-password', { email }),

  resetPassword: (input: { token: string; password: string; confirmPassword: string }) =>
    api.post<{ message: string }>('/api/v1/auth/reset-password', input),

  changePassword: (input: {
    currentPassword: string;
    password: string;
    confirmPassword: string;
  }) => api.post<{ message: string }>('/api/v1/auth/change-password', input),

  acceptInvitation: (input: { token: string; name?: string; password?: string }) =>
    api.post<{ merchantId: string; message: string }>('/api/v1/auth/accept-invitation', input),
};

// -----------------------------------------------------------------------------
// Merchant, onboarding, staff, settings
// -----------------------------------------------------------------------------

export interface OnboardingStateDto {
  readonly step: OnboardingStep;
  readonly progressPercent: number;
  readonly completedAt: string | null;
  readonly hasProduct: boolean;
  readonly hasSalesPage: boolean;
  readonly hasPaymentProvider: boolean;
  readonly hasShipping: boolean;
}

export const merchantService = {
  settings: () => api.get<MerchantSettingsDto>('/api/v1/merchant/settings'),

  updateSettings: (input: UpdateMerchantSettingsInput) =>
    api.patch<MerchantSettingsDto>('/api/v1/merchant/settings', input),

  onboarding: () => api.get<OnboardingStateDto>('/api/v1/merchant/onboarding'),

  /** The store's plan, how long is left, and what it has used. */
  subscription: () => api.get<SubscriptionDto>('/api/v1/merchant/subscription'),

  /** Plans a store can choose between. */
  plans: () => api.get<PlanDto[]>('/api/v1/merchant/plans'),

  billingStatus: () => api.get<{ onlineUpgrades: boolean }>('/api/v1/merchant/billing/status'),

  /** Start paying for a plan; the result carries the page to pay on. */
  startUpgrade: (input: StartUpgradeInput) =>
    api.post<SubscriptionOrderDto>('/api/v1/merchant/billing/checkout', input),

  billingOrders: () => api.get<SubscriptionOrderDto[]>('/api/v1/merchant/billing/orders'),

  billingOrder: (id: string) =>
    api.get<SubscriptionOrderDto>(`/api/v1/merchant/billing/orders/${id}`),

  // Payment methods
  paymentMethods: () => api.get<PaymentMethodDto[]>('/api/v1/merchant/payment-methods'),

  savePaymentMethod: (code: string, input: SavePaymentMethodInput) =>
    api.put<PaymentMethodDto>(`/api/v1/merchant/payment-methods/${code}`, input),

  testPaymentMethod: (code: string) =>
    api.post<PaymentMethodDto & { testMessage: string }>(
      `/api/v1/merchant/payment-methods/${code}/test`,
    ),

  enablePaymentMethod: (code: string, input: EnablePaymentMethodInput) =>
    api.post<PaymentMethodDto>(`/api/v1/merchant/payment-methods/${code}/enable`, input),

  removePaymentMethod: (code: string) =>
    api.delete<PaymentMethodDto>(`/api/v1/merchant/payment-methods/${code}`),

  completeOnboardingStep: (step: OnboardingStep, data: unknown) =>
    api.post<{ step: OnboardingStep; progressPercent: number }>(
      `/api/v1/merchant/onboarding/${step}`,
      data,
    ),

  checkSlug: (slug: string) =>
    api.get<{ slug: string; available: boolean }>('/api/v1/merchant/slug-available', { slug }),

  staff: () => api.get<StaffMemberDto[]>('/api/v1/merchant/staff'),

  invitations: () => api.get<StaffInvitationDto[]>('/api/v1/merchant/staff/invitations'),

  inviteStaff: (input: InviteStaffInput) =>
    api.post<StaffInvitationDto>('/api/v1/merchant/staff/invitations', input),

  revokeInvitation: (id: string) =>
    api.delete<{ ok: true }>(`/api/v1/merchant/staff/invitations/${id}`),

  updateStaffRole: (id: string, role: string) =>
    api.patch<StaffMemberDto>(`/api/v1/merchant/staff/${id}`, { role }),

  removeStaff: (id: string) => api.delete<{ ok: true }>(`/api/v1/merchant/staff/${id}`),

  setStaffOrderEmails: (id: string, receivesOrderEmails: boolean) =>
    api.patch<StaffMemberDto>(`/api/v1/merchant/staff/${id}/notifications`, {
      receivesOrderEmails,
    }),

  shippingZones: () => api.get<ShippingZoneDto[]>('/api/v1/merchant/shipping/zones'),

  createShippingZone: (input: { name: string; stateCodes: string[]; isActive?: boolean }) =>
    api.post<ShippingZoneDto>('/api/v1/merchant/shipping/zones', input),

  updateShippingZone: (
    id: string,
    input: Partial<{ name: string; stateCodes: string[]; isActive: boolean }>,
  ) => api.patch<ShippingZoneDto>(`/api/v1/merchant/shipping/zones/${id}`, input),

  deleteShippingZone: (id: string) =>
    api.delete<{ ok: true }>(`/api/v1/merchant/shipping/zones/${id}`),

  createShippingMethod: (input: Record<string, unknown>) =>
    api.post<ShippingMethodDto>('/api/v1/merchant/shipping/methods', input),

  updateShippingMethod: (id: string, input: Record<string, unknown>) =>
    api.patch<ShippingMethodDto>(`/api/v1/merchant/shipping/methods/${id}`, input),

  deleteShippingMethod: (id: string) =>
    api.delete<{ ok: true }>(`/api/v1/merchant/shipping/methods/${id}`),
};

// -----------------------------------------------------------------------------
// Catalogue
// -----------------------------------------------------------------------------

export const productService = {
  list: (query: Query, signal?: AbortSignal): Promise<ApiResult<ProductDto[]>> =>
    api.getPaged<ProductDto[]>('/api/v1/products', query, signal),

  get: (id: string, signal?: AbortSignal) =>
    api.get<ProductDto>(`/api/v1/products/${id}`, undefined, signal),

  create: (input: CreateProductInput) => api.post<ProductDto>('/api/v1/products', input),

  update: (id: string, input: UpdateProductInput) =>
    api.patch<ProductDto>(`/api/v1/products/${id}`, input),

  remove: (id: string) => api.delete<{ ok: true }>(`/api/v1/products/${id}`),

  adjustStock: (id: string, input: { variantId?: string | null; delta: number; reason?: string }) =>
    api.post<ProductDto>(`/api/v1/products/${id}/stock`, input),
};

export const categoryService = {
  list: () => api.get<CategoryDto[]>('/api/v1/categories'),
  create: (input: { name: string; slug?: string; description?: string | null }) =>
    api.post<CategoryDto>('/api/v1/categories', input),
  remove: (id: string) => api.delete<{ ok: true }>(`/api/v1/categories/${id}`),
};

// -----------------------------------------------------------------------------
// Sales pages
// -----------------------------------------------------------------------------

export const salesPageService = {
  list: (query: Query, signal?: AbortSignal): Promise<ApiResult<SalesPageDto[]>> =>
    api.getPaged<SalesPageDto[]>('/api/v1/sales-pages', query, signal),

  get: (id: string, signal?: AbortSignal) =>
    api.get<SalesPageWithContentDto>(`/api/v1/sales-pages/${id}`, undefined, signal),

  create: (input: CreateSalesPageInput) =>
    api.post<SalesPageWithContentDto>('/api/v1/sales-pages', input),

  update: (id: string, input: Record<string, unknown>) =>
    api.patch<SalesPageWithContentDto>(`/api/v1/sales-pages/${id}`, input),

  saveContent: (id: string, content: SalesPageContent, changeNote?: string) =>
    api.put<SalesPageWithContentDto>(`/api/v1/sales-pages/${id}/content`, {
      content,
      ...(changeNote ? { changeNote } : {}),
    }),

  publish: (id: string) => api.post<SalesPageDto>(`/api/v1/sales-pages/${id}/publish`),

  unpublish: (id: string) => api.post<SalesPageDto>(`/api/v1/sales-pages/${id}/unpublish`),

  duplicate: (id: string, title?: string) =>
    api.post<SalesPageWithContentDto>(
      `/api/v1/sales-pages/${id}/duplicate`,
      title ? { title } : {},
    ),

  remove: (id: string) => api.delete<{ ok: true }>(`/api/v1/sales-pages/${id}`),
};

// -----------------------------------------------------------------------------
// Orders, payments, invoices
// -----------------------------------------------------------------------------

export const orderService = {
  list: (query: Query, signal?: AbortSignal): Promise<ApiResult<OrderSummaryDto[]>> =>
    api.getPaged<OrderSummaryDto[]>('/api/v1/orders', query, signal),

  get: (id: string, signal?: AbortSignal) =>
    api.get<OrderDto>(`/api/v1/orders/${id}`, undefined, signal),

  update: (id: string, input: Record<string, unknown>) =>
    api.patch<OrderDto>(`/api/v1/orders/${id}`, input),

  /** Confirm a bank-transfer / QR payment has arrived. */
  markPaid: (id: string) =>
    api.post<{ status: string }>(`/api/v1/orders/${id}/mark-paid`, {}),

  setStatus: (id: string, status: string, note?: string) =>
    api.post<OrderDto>(`/api/v1/orders/${id}/status`, { status, ...(note ? { note } : {}) }),

  createShipment: (id: string, input: Record<string, unknown>) =>
    api.post<OrderDto>(`/api/v1/orders/${id}/shipment`, input),

  refund: (id: string, input: { amount?: string; reason: string }) =>
    // A fresh key per click, so a genuine second refund is still possible but
    // an accidental double-submit of the same click is not.
    api.post<{ refundedAmount: string; status: string; order: OrderDto }>(
      `/api/v1/orders/${id}/refund`,
      input,
      newIdempotencyKey(),
    ),
};

// -----------------------------------------------------------------------------
// Customers & coupons
// -----------------------------------------------------------------------------

export const customerService = {
  list: (query: Query, signal?: AbortSignal): Promise<ApiResult<CustomerDto[]>> =>
    api.getPaged<CustomerDto[]>('/api/v1/customers', query, signal),

  get: (id: string, signal?: AbortSignal) =>
    api.get<CustomerDto>(`/api/v1/customers/${id}`, undefined, signal),

  orders: (id: string) => api.get<OrderSummaryDto[]>(`/api/v1/customers/${id}/orders`),

  notes: (id: string) => api.get<CustomerNoteDto[]>(`/api/v1/customers/${id}/notes`),

  addNote: (id: string, body: string) =>
    api.post<CustomerNoteDto>(`/api/v1/customers/${id}/notes`, { body }),

  create: (input: CreateCustomerInput) => api.post<CustomerDto>('/api/v1/customers', input),

  update: (id: string, input: Record<string, unknown>) =>
    api.patch<CustomerDto>(`/api/v1/customers/${id}`, input),
};

export const couponService = {
  list: (query: Query, signal?: AbortSignal): Promise<ApiResult<CouponDto[]>> =>
    api.getPaged<CouponDto[]>('/api/v1/coupons', query, signal),

  get: (id: string) => api.get<CouponDto>(`/api/v1/coupons/${id}`),

  create: (input: CreateCouponInput) => api.post<CouponDto>('/api/v1/coupons', input),

  update: (id: string, input: UpdateCouponInput) =>
    api.patch<CouponDto>(`/api/v1/coupons/${id}`, input),

  remove: (id: string) => api.delete<{ ok: true }>(`/api/v1/coupons/${id}`),
};

// -----------------------------------------------------------------------------
// Reports
// -----------------------------------------------------------------------------

export const reportService = {
  dashboard: (query: Query, signal?: AbortSignal) =>
    api.get<DashboardMetricsDto>('/api/v1/reports/dashboard', query, signal),
};

// -----------------------------------------------------------------------------
// Public storefront (no authentication)
// -----------------------------------------------------------------------------

// The JSON API lives under /api/v1/store/* — not the bare /store/* the
// browser's address bar shows for these pages. Both the API and the SPA's
// own React Router routes use the plain /store/:slug/... path, so if the
// fetch calls below used the same bare prefix, a reverse proxy would have no
// way to tell "return the HTML shell" apart from "return the JSON" on an
// identical URL (see apps/api/src/app.ts for the registration-side note).
export const storefrontService = {
  store: (slug: string, signal?: AbortSignal) =>
    api.get<PublicStoreDto>(`/api/v1/store/${slug}`, undefined, signal),

  products: (slug: string, query: Query, signal?: AbortSignal) =>
    api.getPaged<PublicProductDto[]>(`/api/v1/store/${slug}/products`, query, signal),

  product: (slug: string, productSlug: string, signal?: AbortSignal) =>
    api.get<{ product: PublicProductDto; store: PublicStoreDto }>(
      `/api/v1/store/${slug}/product/${productSlug}`,
      undefined,
      signal,
    ),

  salesPage: (slug: string, pageSlug: string, signal?: AbortSignal) =>
    api.get<PublicSalesPageDto>(`/api/v1/store/${slug}/page/${pageSlug}`, undefined, signal),

  paymentMethods: (slug: string) =>
    api.get<PublicPaymentMethodDto[]>(`/api/v1/store/${slug}/payment-methods`),

  shippingMethods: (slug: string) =>
    api.get<(ShippingMethodDto & { stateCodes: string[] })[]>(
      `/api/v1/store/${slug}/shipping-methods`,
    ),

  previewCheckout: (slug: string, input: PreviewCheckoutInput) =>
    api.post<CheckoutPreviewDto>(`/api/v1/store/${slug}/checkout/preview`, input),

  validateCoupon: (slug: string, input: Record<string, unknown>) =>
    api.post<{ code: string; valid: boolean; discountAmount?: string; message?: string | null }>(
      `/api/v1/store/${slug}/checkout/validate-coupon`,
      input,
    ),

  checkout: (slug: string, input: CreateCheckoutInput, idempotencyKey: string) =>
    api.post<CheckoutResultDto>(`/api/v1/store/${slug}/checkout`, input, idempotencyKey),

  /** The sandbox gateway's hosted page settling a payment. See PaymentService. */
  completeSandboxPayment: (reference: string, outcome: 'success' | 'failure' | 'cancelled') =>
    api.post<{ status: string; orderNumber: string; merchantSlug: string }>(
      `/api/v1/payments/sandbox/${encodeURIComponent(reference)}/complete`,
      { outcome },
    ),

  trackOrder: (slug: string, orderNumber: string, email: string) =>
    api.get<CheckoutResultDto['order']>(`/api/v1/store/${slug}/orders/${orderNumber}`, { email }),
};

// -----------------------------------------------------------------------------
// Super Admin (platform) — rejected by the API for anyone who is not a platform
// operator, whatever the UI shows.
// -----------------------------------------------------------------------------

export const adminService = {
  overview: (query: Query, signal?: AbortSignal) =>
    api.get<PlatformOverviewDto>('/api/v1/admin/overview', query, signal),

  stores: (query: Query, signal?: AbortSignal): Promise<ApiResult<AdminStoreRowDto[]>> =>
    api.getPaged<AdminStoreRowDto[]>('/api/v1/admin/stores', query, signal),

  store: (id: string, signal?: AbortSignal) =>
    api.get<AdminStoreDetailDto>(`/api/v1/admin/stores/${id}`, undefined, signal),

  /** Suspend a store (a reason is required) or lift its suspension. */
  setStoreStatus: (id: string, status: 'ACTIVE' | 'SUSPENDED', reason?: string) =>
    api.patch<AdminStoreDetailDto>(`/api/v1/admin/stores/${id}/status`, {
      status,
      ...(reason ? { reason } : {}),
    }),

  plans: () => api.get<AdminPlanDto[]>('/api/v1/admin/plans'),

  billingSettings: () => api.get<PlatformBillingSettingsDto>('/api/v1/admin/billing/settings'),

  saveBillingSettings: (input: SavePlatformBillingInput) =>
    api.put<PlatformBillingSettingsDto>('/api/v1/admin/billing/settings', input),

  testBilling: () => api.post<{ ok: boolean; message: string }>('/api/v1/admin/billing/test'),

  billingOrders: (query: Query): Promise<ApiResult<SubscriptionOrderDto[]>> =>
    api.getPaged<SubscriptionOrderDto[]>('/api/v1/admin/billing/orders', query),


  updatePlan: (id: string, input: UpdatePlanInput) =>
    api.patch<AdminPlanDto>(`/api/v1/admin/plans/${id}`, input),

  storeSubscription: (storeId: string) =>
    api.get<SubscriptionDto>(`/api/v1/admin/stores/${storeId}/subscription`),

  assignStorePlan: (storeId: string, input: AssignPlanInput) =>
    api.put<SubscriptionDto>(`/api/v1/admin/stores/${storeId}/subscription`, input),

  /** Activate someone whose confirmation email never arrived. */
  activateStoreUser: (storeId: string, userId: string) =>
    api.post<AdminStoreDetailDto>(`/api/v1/admin/stores/${storeId}/users/${userId}/activate`, {}),

  /** The curated event log: jumps, suspensions, security, refunds. */
  activity: (query: Query, signal?: AbortSignal): Promise<ApiResult<AdminActivityDto[]>> =>
    api.getPaged<AdminActivityDto[]>('/api/v1/admin/activity', query, signal),

  /** Every time an operator opened a store. */
  accessLog: (query: Query, signal?: AbortSignal): Promise<ApiResult<SupportAccessLogDto[]>> =>
    api.getPaged<SupportAccessLogDto[]>('/api/v1/admin/access-log', query, signal),

  /** The platform team, and who is copied on merchants' paid orders. */
  team: () => api.get<PlatformUserDto[]>('/api/v1/admin/team'),

  setCcOrderEmails: (id: string, ccOrderEmails: boolean) =>
    api.patch<PlatformUserDto>(`/api/v1/admin/team/${id}/cc-order-emails`, { ccOrderEmails }),

  /** Open a store, view-only, for a limited time. The reason is recorded. */
  startSupportAccess: (merchantId: string, reason: string) =>
    api.post<SupportAccessDto>(`/api/v1/admin/stores/${merchantId}/access`, { reason }),

  endSupportAccess: (grantId: string) =>
    api.delete<{ ok: true }>(`/api/v1/admin/access/${grantId}`),
};
